import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ClassAssignmentPicker,
  ConfirmDialog,
  HistoryTimeline,
  InlineAlert,
  Loader,
  LoadingButton,
  SideDrawer,
  SourceBadge,
  StatusChip,
} from "src/grading/components";
import { PickedClass } from "src/grading/components/ClassAssignmentPicker/ClassAssignmentPicker";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { permissions } from "src/grading/utils/permissionsHelpers";
import { ApiError } from "src/models";
import { HistoryPageArgs } from "src/models/history";
import {
  ClassAssignment,
  StaffDetail,
  StaffRole,
  UpdateStaffPayload,
  fullName,
  primaryRole,
} from "src/models/staff";
import {
  useDeactivateStaffMutation,
  useGetStaffHistoryQuery,
  useGetStaffQuery,
  useReactivateStaffMutation,
  useResetStaffPasswordMutation,
  useSetStaffClassesMutation,
  useUpdateStaffMutation,
} from "src/store/reducers/staff/staffApiSlice";
import usePeopleScope from "./usePeopleScope";

export interface PersonPanelProps {
  profileId: string;
  onClose: () => void;
  /** The platform admin's chosen district, for the class search. */
  districtId?: string;
}

const needsSchool = (role: StaffRole | "") =>
  role === "school_admin" || role === "teacher";

const toPicked = (assignment: ClassAssignment): PickedClass => ({
  classId: assignment.classId,
  className: assignment.className,
  schoolName: assignment.schoolName,
  role: assignment.role,
});

type Notice = { severity: "success" | "error"; text: string };

/** The error copy for a refused or broken request. */
const useErrorText = () => {
  const { t } = useTranslation();
  return (error: unknown, fallback: string) => {
    const code = refusalCode(error);
    return code ? refusalCopy(t, code) : messageOf(error, fallback);
  };
};

const Section = ({
  title,
  children,
  id,
}: {
  title: string;
  children: ReactNode;
  id: string;
}) => (
  <Box component="section" aria-labelledby={id} sx={{ mb: 3 }}>
    <Typography id={id} variant="h4" component="h3" sx={{ mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const ReadOnlyRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: 2,
      py: 0.75,
      fontSize: 14,
    }}
  >
    <Box component="dt" sx={{ fontWeight: 700 }}>
      {label}
    </Box>
    <Box
      component="dd"
      sx={{ m: 0, color: "text.secondary", textAlign: "right" }}
    >
      {value}
    </Box>
  </Box>
);

/** Staff member detail: Details, Classes, History and the actions the server allows. */
export const PersonPanel = ({
  profileId,
  onClose,
  districtId,
}: PersonPanelProps) => {
  const { t, i18n } = useTranslation();
  const uid = useId();
  const errorText = useErrorText();
  const scope = usePeopleScope(districtId);
  const query = useGetStaffQuery(profileId);
  const { data: person, isLoading, isError, error, refetch } = query;

  const [updateStaff, { isLoading: saving }] = useUpdateStaffMutation();
  const [deactivateStaff, { isLoading: deactivating }] =
    useDeactivateStaffMutation();
  const [reactivateStaff, { isLoading: reactivating }] =
    useReactivateStaffMutation();
  const [resetPassword, { isLoading: resetting }] =
    useResetStaffPasswordMutation();
  const [setStaffClasses, { isLoading: savingClasses }] =
    useSetStaffClassesMutation();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState<StaffRole | "">("");
  const [organizationId, setOrganizationId] = useState("");
  const [stale, setStale] = useState<Partial<StaffDetail> | null>(null);
  const [detailsNotice, setDetailsNotice] = useState<Notice | null>(null);
  const [editingClasses, setEditingClasses] = useState(false);
  const [picked, setPicked] = useState<PickedClass[]>([]);
  const [classesNotice, setClassesNotice] = useState<Notice | null>(null);
  const [restorable, setRestorable] = useState<ClassAssignment[]>([]);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [confirm, setConfirm] = useState<"deactivate" | "reset" | null>(null);

  // Load the form from the record, and again only when the record's version changes (a save,
  // a reload), so a refetch of the same version keeps what the viewer is typing.
  const loadedVersion = useRef<string | null>(null);
  useEffect(() => {
    if (!person) return;
    const version = `${person.id}:${person.updatedAt}`;
    if (loadedVersion.current === version) return;
    loadedVersion.current = version;
    setFirstName(person.firstName);
    setLastName(person.lastName);
    setRole(primaryRole(person.roles) ?? "");
    setOrganizationId(person.organization?.id ?? "");
  }, [person]);

  const name = person ? fullName(person) : "";
  const roleName = (value: string) => t(`roles.${value}`);
  const activeAssignments = (person?.assignments ?? []).filter(
    (assignment) => assignment.active,
  );
  const canEditClasses =
    scope.has(permissions.UPDATE_CLASSES) && person?.status !== "deactivated";
  const canReadHistory = scope.has(permissions.READ_AUDIT);
  const useHistoryPage = (page: Required<HistoryPageArgs>) =>
    useGetStaffHistoryQuery({ profileId, ...page });

  const saveDetails = async () => {
    if (!person) return;
    setDetailsNotice(null);
    setStale(null);
    const body: UpdateStaffPayload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      expectedUpdatedAt: person.updatedAt,
    };
    if (role && role !== primaryRole(person.roles)) body.roles = [role];
    let nextOrg = organizationId;
    if (role === "district_admin" && person.organization?.type === "school") {
      nextOrg = scope.districtId ?? nextOrg;
    }
    if (nextOrg && nextOrg !== person.organization?.id) {
      body.organizationId = nextOrg;
    }
    try {
      await updateStaff({ profileId, body }).unwrap();
      setDetailsNotice({ severity: "success", text: t("staff.panel.saved") });
    } catch (err) {
      if (refusalCode(err) === "STALE_RECORD") {
        const current = (err as ApiError).data?.data?.current;
        setStale((current as Partial<StaffDetail>) ?? {});
        return;
      }
      setDetailsNotice({
        severity: "error",
        text: errorText(err, t("staff.panel.saveError")),
      });
    }
  };

  const reload = async () => {
    setStale(null);
    setDetailsNotice(null);
    await refetch();
  };

  const saveClasses = async (
    next: PickedClass[],
    removedClassIds?: string[],
  ) => {
    setClassesNotice(null);
    try {
      await setStaffClasses({
        profileId,
        assignments: next.map((item) => ({
          classId: item.classId,
          role: item.role,
        })),
        ...(removedClassIds?.length ? { removedClassIds } : {}),
      }).unwrap();
      setEditingClasses(false);
      setRestorable([]);
      setClassesNotice({
        severity: "success",
        text: t("staff.panel.classesSaved"),
      });
    } catch (err) {
      setClassesNotice({
        severity: "error",
        text: errorText(err, t("staff.panel.classesError")),
      });
    }
  };

  const restore = () => {
    const merged = [...activeAssignments.map(toPicked)];
    restorable.forEach((assignment) => {
      if (!merged.some((item) => item.classId === assignment.classId)) {
        merged.push(toPicked(assignment));
      }
    });
    return saveClasses(merged);
  };

  const runAction = async (action: () => Promise<unknown>, success: string) => {
    setNotice(null);
    try {
      await action();
      setNotice({ severity: "success", text: success });
    } catch (err) {
      setNotice({
        severity: "error",
        text: errorText(err, t("staff.panel.actionError")),
      });
    }
  };

  const deactivate = async () => {
    await runAction(
      () => deactivateStaff(profileId).unwrap(),
      t("staff.panel.deactivated", { name }),
    );
    setConfirm(null);
  };

  const reset = async () => {
    await runAction(
      () => resetPassword(profileId).unwrap(),
      t("staff.panel.resetDone", { email: person?.email }),
    );
    setConfirm(null);
  };

  const reactivate = () =>
    runAction(
      async () => {
        const result = await reactivateStaff(profileId).unwrap();
        setRestorable(result.inactiveAssignments ?? []);
      },
      t("staff.panel.reactivated", { name }),
    );

  const allowed = person?.allowedActions;
  const showDeactivate = Boolean(
    allowed?.deactivate && person?.status !== "deactivated",
  );
  const showReactivate = Boolean(allowed?.reactivate);
  const showReset = Boolean(allowed?.resetPassword && !person?.sso);
  const footer =
    person && (showDeactivate || showReactivate || showReset) ? (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {showReset ? (
          <Button variant="outlined" onClick={() => setConfirm("reset")}>
            {t("staff.panel.resetPassword")}
          </Button>
        ) : null}
        {showReactivate ? (
          <LoadingButton
            variant="outlined"
            loading={reactivating}
            onClick={reactivate}
            label={t("staff.panel.reactivate")}
          />
        ) : null}
        {showDeactivate ? (
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirm("deactivate")}
            sx={{ ml: "auto" }}
          >
            {t("staff.panel.deactivate")}
          </Button>
        ) : null}
      </Stack>
    ) : undefined;

  const roleOptions: StaffRole[] = allowed
    ? [
        ...(role && !allowed.grantableRoles.includes(role) ? [role] : []),
        ...allowed.grantableRoles,
      ]
    : [];
  const schoolOptions = scope.schools;
  const pickSchool = needsSchool(role) && schoolOptions.length > 0;
  const schoolMissing =
    pickSchool && !schoolOptions.some((school) => school.id === organizationId);

  const staleSummary = stale
    ? [
        stale.firstName || stale.lastName
          ? fullName({
              firstName: stale.firstName ?? "",
              lastName: stale.lastName ?? "",
            })
          : null,
        stale.roles?.length ? roleName(primaryRole(stale.roles) ?? "") : null,
        stale.organization?.name ?? null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const body = (() => {
    if (isLoading) return <Loader />;
    if (isError || !person) {
      return (
        <InlineAlert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {t("common.retry")}
            </Button>
          }
        >
          {messageOf(error, t("staff.panel.loadError"))}
        </InlineAlert>
      );
    }
    return (
      <>
        {notice ? (
          <Box sx={{ mb: 2 }}>
            <InlineAlert severity={notice.severity}>{notice.text}</InlineAlert>
          </Box>
        ) : null}

        <Section id={`${uid}-details`} title={t("staff.panel.details")}>
          {allowed?.edit ? (
            <Stack spacing={2}>
              <TextField
                label={t("classes.firstName")}
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                inputProps={{ maxLength: 100 }}
                size="small"
              />
              <TextField
                label={t("classes.lastName")}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                inputProps={{ maxLength: 100 }}
                size="small"
              />
              <TextField
                select
                size="small"
                label={t("staff.panel.role")}
                value={role}
                onChange={(event) => setRole(event.target.value as StaffRole)}
              >
                {roleOptions.map((option) => (
                  <MenuItem
                    key={option}
                    value={option}
                    disabled={!allowed.grantableRoles.includes(option)}
                  >
                    {roleName(option)}
                  </MenuItem>
                ))}
              </TextField>
              {pickSchool ? (
                <TextField
                  select
                  size="small"
                  label={t("staff.panel.school")}
                  value={schoolMissing ? "" : organizationId}
                  onChange={(event) => setOrganizationId(event.target.value)}
                  error={schoolMissing}
                  helperText={
                    schoolMissing ? t("invite.schoolRequired") : undefined
                  }
                >
                  {schoolOptions.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <Box component="dl" sx={{ m: 0 }}>
                <ReadOnlyRow
                  label={t("staff.panel.email")}
                  value={person.email}
                />
                <ReadOnlyRow
                  label={t("staff.panel.signIn")}
                  value={
                    person.sso
                      ? t("staff.panel.ssoSignIn")
                      : t("staff.panel.passwordSignIn")
                  }
                />
              </Box>
              {stale ? (
                <InlineAlert
                  severity="warning"
                  action={
                    <Button color="inherit" size="small" onClick={reload}>
                      {t("staff.panel.reload")}
                    </Button>
                  }
                >
                  {refusalCopy(t, "STALE_RECORD")}
                  {staleSummary ? (
                    <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                      {t("staff.panel.staleCurrent", { summary: staleSummary })}
                    </Box>
                  ) : null}
                </InlineAlert>
              ) : null}
              {detailsNotice ? (
                <InlineAlert severity={detailsNotice.severity}>
                  {detailsNotice.text}
                </InlineAlert>
              ) : null}
              <Box>
                <LoadingButton
                  variant="contained"
                  loading={saving}
                  disabled={
                    !firstName.trim() || !lastName.trim() || schoolMissing
                  }
                  onClick={saveDetails}
                  label={t("staff.panel.save")}
                />
              </Box>
            </Stack>
          ) : (
            <Box component="dl" sx={{ m: 0 }}>
              <ReadOnlyRow
                label={t("staff.panel.email")}
                value={person.email}
              />
              <ReadOnlyRow
                label={t("staff.panel.role")}
                value={person.roles.map(roleName).join(", ")}
              />
              <ReadOnlyRow
                label={t("staff.panel.school")}
                value={person.organization?.name ?? "—"}
              />
              <ReadOnlyRow
                label={t("staff.panel.signIn")}
                value={
                  person.sso
                    ? t("staff.panel.ssoSignIn")
                    : t("staff.panel.passwordSignIn")
                }
              />
              <ReadOnlyRow
                label={t("staff.panel.lastActive")}
                value={
                  person.lastActiveAt
                    ? new Date(person.lastActiveAt).toLocaleDateString(
                        i18n.language,
                      )
                    : t("staff.lastActiveNever")
                }
              />
            </Box>
          )}
        </Section>

        <Section id={`${uid}-classes`} title={t("staff.panel.classes")}>
          {editingClasses ? (
            <Stack spacing={1.5}>
              <ClassAssignmentPicker
                mode="byPerson"
                label={t("staff.assign.classes")}
                districtId={scope.requestDistrictId}
                value={picked}
                onChange={setPicked}
              />
              <Stack direction="row" spacing={1}>
                <LoadingButton
                  variant="contained"
                  loading={savingClasses}
                  onClick={() =>
                    saveClasses(
                      picked,
                      activeAssignments
                        .filter(
                          (assignment) =>
                            !picked.some(
                              (item) => item.classId === assignment.classId,
                            ),
                        )
                        .map((assignment) => assignment.classId),
                    )
                  }
                  label={t("staff.panel.saveClasses")}
                />
                <Button
                  color="inherit"
                  onClick={() => setEditingClasses(false)}
                  disabled={savingClasses}
                >
                  {t("common.cancel")}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {activeAssignments.length === 0 ? (
                <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                  {t("staff.panel.noClasses")}
                </Typography>
              ) : (
                <Stack
                  component="ul"
                  aria-label={t("staff.panel.classes")}
                  spacing={1}
                  sx={{ m: 0, p: 0, listStyle: "none" }}
                >
                  {activeAssignments.map((assignment) => (
                    <Box
                      component="li"
                      key={assignment.classId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontWeight: 700 }}>
                          {assignment.className}
                        </Box>
                        <Box sx={{ fontSize: 13, color: "text.secondary" }}>
                          {t("staff.panel.classLine", {
                            role: t(`staff.assignmentRole.${assignment.role}`),
                            school: assignment.schoolName,
                          })}
                        </Box>
                      </Box>
                      <SourceBadge source={assignment.source} />
                    </Box>
                  ))}
                </Stack>
              )}
              {canEditClasses ? (
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setPicked(activeAssignments.map(toPicked));
                      setClassesNotice(null);
                      setEditingClasses(true);
                    }}
                  >
                    {t("staff.panel.editClasses")}
                  </Button>
                </Box>
              ) : null}
            </Stack>
          )}
          {restorable.length > 0 ? (
            <Box
              component="section"
              aria-labelledby={`${uid}-restore`}
              sx={(theme) => ({
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: theme.palette.surfaceAlt,
              })}
            >
              <Typography
                id={`${uid}-restore`}
                component="h4"
                sx={{ fontWeight: 800, fontSize: 15, mb: 0.5 }}
              >
                {t("staff.panel.restoreTitle")}
              </Typography>
              <Typography sx={{ fontSize: 14, mb: 1 }}>
                {t("staff.panel.restoreBody", { name })}
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 2.5 }}>
                {restorable.map((assignment) => (
                  <li key={assignment.classId}>
                    {assignment.className} ·{" "}
                    {t(`staff.assignmentRole.${assignment.role}`)}
                  </li>
                ))}
              </Box>
              <LoadingButton
                variant="contained"
                loading={savingClasses}
                onClick={restore}
                label={t("staff.panel.restore")}
              />
            </Box>
          ) : null}
          {classesNotice ? (
            <Box sx={{ mt: 1.5 }}>
              <InlineAlert severity={classesNotice.severity}>
                {classesNotice.text}
              </InlineAlert>
            </Box>
          ) : null}
        </Section>

        {canReadHistory ? (
          <Section id={`${uid}-history`} title={t("history.title")}>
            <HistoryTimeline subjectName={name} usePage={useHistoryPage} />
          </Section>
        ) : null}
      </>
    );
  })();

  return (
    <>
      <SideDrawer
        open
        onClose={onClose}
        width={480}
        title={name || t("common.loading")}
        subtitle={
          person ? (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
            >
              <span>{person.email}</span>
              <StatusChip status={person.status} />
            </Box>
          ) : undefined
        }
        footer={footer}
      >
        {body}
      </SideDrawer>
      {person ? (
        <>
          <ConfirmDialog
            open={confirm === "deactivate"}
            tone="destructive"
            title={t("staff.panel.deactivateTitle", { name })}
            body={t("staff.panel.deactivateBody", { name })}
            confirmLabel={t("staff.panel.deactivate")}
            typedConfirmation={
              person.roles.includes("district_admin") ? person.email : undefined
            }
            loading={deactivating}
            onConfirm={deactivate}
            onClose={() => setConfirm(null)}
          />
          <ConfirmDialog
            open={confirm === "reset"}
            title={t("staff.panel.resetTitle", { name })}
            body={t("staff.panel.resetBody")}
            confirmLabel={t("staff.panel.resetPassword")}
            loading={resetting}
            onConfirm={reset}
            onClose={() => setConfirm(null)}
          />
        </>
      ) : null}
    </>
  );
};

export default PersonPanel;
